import React from 'react'
import { formFields } from '../constants'
import { makeStyles } from '@material-ui/styles'
import { Box, Tooltip, Typography, Avatar } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import VisibilityOutlinedIcon from 'components/material-symbol/icons/Visibility'
import VisibilityOffOutlinedIcon from 'components/material-symbol/icons/VisibilityOff'
import LocationOnOutlinedIcon from 'components/material-symbol/icons/LocationOn'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import EnhancedTable from 'components/tables/enhanced-table'
import SectionEmptyState from 'components/section-empty-state'
import { FormattedOneLineAddress } from 'components/util/formatted_address'

const useStyles = makeStyles(theme => ({
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  listEmptyStateContent: {
    padding: theme.spacing(4, 2),
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  facilityAvatar: {
    height: 36,
    width: 36,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  visibilityStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    marginRight: theme.spacing(2),
  },
}))

const ProfileFacilityListItem = ({ facility }) => {
  const styles = useStyles()
  const isPrimary = facility.customData?.facilityRank === 1
  const isVisible = Boolean(facility.customData?.visible)
  const address = facility.street ? (
    <FormattedOneLineAddress
      address1={facility.street}
      city={facility.city}
      state={facility.state}
      zip={facility.zipCode}
    />
  ) : null
  const visibilityLabel = isVisible
    ? 'Visible on Coach Profile'
    : 'Hidden on Coach Profile'

  return (
    <TableRow data-testid={`profile-facility-row-${facility.facilityId}`}>
      <TableCell className={styles.info}>
        <Box className={styles.nameCell}>
          <Avatar className={styles.facilityAvatar}>
            <LocationOnOutlinedIcon />
          </Avatar>
          <Box className={styles.info}>
            <Typography className={styles.text} noWrap>
              {facility.facilityName}
            </Typography>
            {address && (
              <Typography className={styles.textSecondary} noWrap>
                {address}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell className={styles.actions}>
        <Box className={styles.actionButtons}>
          {isPrimary && (
            <Tooltip
              title="The primary facility is used for location-based searches and cannot be hidden or deleted."
              placement="bottom"
            >
              <span className={styles.statusChip}>Primary</span>
            </Tooltip>
          )}
          <Tooltip title={visibilityLabel} placement="bottom">
            <Box
              className={styles.visibilityStatus}
              aria-label={visibilityLabel}
              role="img"
            >
              {isVisible ? (
                <VisibilityOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOffOutlinedIcon fontSize="small" />
              )}
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  )
}

const columns = [
  {
    id: 'name',
    label: 'Name',
    align: 'left',
    width: '80%',
    mobile: true,
    sortable: false,
  },
  {
    id: 'actions',
    label: '',
    align: 'right',
    width: '20%',
    mobile: true,
    sortable: false,
  },
]

const Facilities = ({ formFields }) => {
  const classes = useStyles()
  const facilities = formFields.facilities || []
  const rows = facilities.map(facility => (
    <ProfileFacilityListItem
      facility={facility}
      key={facility.facilityId || facility.facilityName}
    />
  ))

  return (
    <Box className={classes.tableShell}>
      <Box className={classes.tableContent}>
        <EnhancedTable
          rows={rows}
          columns={columns}
          label="Profile facilities"
          numberOfRows={Math.max(facilities.length, 1)}
          variant="list"
          rowHeight="72px"
          showHeaderPagination={false}
          showPaginator={false}
          emptyState={() => (
            <Box className={classes.listEmptyStateContent}>
              <SectionEmptyState
                Icon={RoomOutlinedIcon}
                title="Facilities"
                caption="No facilities on your profile yet."
                bordered={false}
              />
            </Box>
          )}
        />
      </Box>
    </Box>
  )
}

Facilities.propTypes = {
  formFields: formFields,
}

Facilities.defaultProps = {
  formFields: {
    facilities: [],
  },
}

export default Facilities
